import * as Sentry from 'lib/client/sentry/sentry';

/**
 * Global application settings interface
 * Extensible for future settings beyond maintenance mode
 */
export interface GlobalSettings {
  isMaintenanceModeEnabled: boolean;
}

/**
 * Default settings used when API request fails or returns malformed data
 * Defaults to maintenance mode OFF for safety
 */
const DEFAULT_SETTINGS: GlobalSettings = {
  isMaintenanceModeEnabled: false,
};

/**
 * Build API URL without React Router basename
 * Returns an absolute URL that bypasses the React Router basename (market prefix like /us/)
 */
const buildApiUrl = (path: string): string => {
  const { protocol, host } = window.location;
  return `${protocol}//${host}${path}`;
};

/**
 * Fetches global application settings from the API
 *
 * @returns Promise resolving to GlobalSettings
 * @throws Never throws - returns DEFAULT_SETTINGS on any error
 *
 * Error handling strategy:
 * - In development: Returns mock settings from settings.mock.ts
 * - In production: Fetches from /api/tintin/settings
 * - Network failures -> DEFAULT_SETTINGS (maintenance OFF)
 * - Malformed response -> DEFAULT_SETTINGS (maintenance OFF)
 * - Invalid JSON -> DEFAULT_SETTINGS (maintenance OFF)
 */
export const fetchGlobalSettings = async (): Promise<GlobalSettings> => {
  // Development mode: Use mock settings from settings.mock.ts
  if (process.env.NODE_ENV === 'development') {
    const { mockSettings } = await import('./settings.mock');

    /* eslint-disable */
    console.log('[Settings] Development mode - using mock settings from settings.mock.ts');
    console.log('[Settings] Mock settings:', mockSettings);
    /* eslint-enable */

    return mockSettings;
  }

  // Production mode: Fetch from API
  try {
    // Build URL that bypasses React Router basename
    const apiUrl = buildApiUrl('/api/tintin/settings');

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin', // Include cookies for same-origin requests
    });

    if (!response.ok) {
      Sentry.captureException(new Error(`Failed to fetch global settings: ${response.status} ${response.statusText}`));
      return DEFAULT_SETTINGS;
    }

    const data = await response.json();

    // Validate response structure
    if (typeof data !== 'object' || data === null) {
      Sentry.captureException(new Error('Global settings response is not an object'));
      return DEFAULT_SETTINGS;
    }

    // Validate isMaintenanceModeEnabled field
    if (typeof data.isMaintenanceModeEnabled !== 'boolean') {
      Sentry.captureException(
        new Error(
          `Global settings malformed: isMaintenanceModeEnabled is ${typeof data.isMaintenanceModeEnabled}, expected boolean`,
        ),
      );
      return DEFAULT_SETTINGS;
    }

    return {
      isMaintenanceModeEnabled: data.isMaintenanceModeEnabled,
    };
  } catch (error) {
    // Network error, JSON parse error, or any other unexpected error
    Sentry.captureException(error);
    return DEFAULT_SETTINGS;
  }
};
